import { RightPanelTransition } from "../RightPanelTransition";
import {
  DRAWER_TRANSITION_MS,
  RIGHT_PANEL_WIDTH,
  RIGHT_PANEL_DOCKED_SX,
  TRACKER_HEADER_SX,
} from "../trackerLayout";
import { usePassPlanner } from "../PassPlanner/PassPlannerContext";
import { useEffect, useMemo, useRef } from "react";
import type { KeyboardEvent } from "react";
import {
  Box,
  Typography,
  ButtonBase,
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import { SatelliteTleData, SatelliteGeneration } from "../SatDataLoader/types";

const GENERATION_LABELS: Record<SatelliteGeneration, string> = {
  bluewalker3: "Prototype",
  block1: "Block 1",
  block2: "Block 2",
};

const getShortName = (name: string): string => {
  if (name.toLowerCase().includes("bluewalker")) return "BW3";
  const match = name.match(/(?:BLUEBIRD\s*|SPACEMOBILE-0*)(\d+)/i);
  if (match) return `BB${Number(match[1])}`;
  return name;
};

const getDisplayName = (name: string): string => {
  if (name.toLowerCase().includes("bluewalker")) return "BlueWalker 3";
  const match = name.match(/(?:BLUEBIRD\s*|SPACEMOBILE-0*)(\d+)/i);
  if (match) return `Bluebird ${Number(match[1])}`;
  return name;
};

type Props = {
  satellites: SatelliteTleData[];
  selectedSatelliteId: number | null;
  onFocusSatellite: (
    satellite: SatelliteTleData,
    anchorEl: HTMLElement,
  ) => void;
};

export const SatelliteListPanel = ({
  satellites,
  selectedSatelliteId,
  onFocusSatellite,
}: Props) => {
  const theme = useTheme();
  const { satellitesOpen, closeSatellites, switchingPanels } = usePassPlanner();
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const pendingSelection = useRef<SatelliteTleData | null>(null);

  useEffect(() => {
    if (!isDesktop) {
      closeSatellites();
      pendingSelection.current = null;
    }
  }, [isDesktop, closeSatellites]);

  const handleSatelliteSelect = (
    satellite: SatelliteTleData,
    anchorEl: HTMLElement,
  ) => {
    if (isDesktop) {
      pendingSelection.current = satellite;
      closeSatellites();
      return;
    }
    onFocusSatellite(satellite, anchorEl);
  };

  const handlePanelExited = () => {
    const satellite = pendingSelection.current;
    pendingSelection.current = null;
    const trigger = document.getElementById("satellites-toggle");
    if (satellite && trigger) onFocusSatellite(satellite, trigger);
  };

  const handleClosePanel = () => {
    closeSatellites();
    document.getElementById("satellites-toggle")?.focus();
  };

  const sortedSatellites = useMemo(
    () => [...satellites].sort((a, b) => a.info.order - b.info.order),
    [satellites],
  );

  const panel = (
    <Box
      component="section"
      aria-label="Satellite list"
      sx={{
        width: "100%",
        height: { md: "100%" },
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        bgcolor: "background.paper",
        pb: "env(safe-area-inset-bottom, 0px)",
        pr: "env(safe-area-inset-right, 0px)",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 2.5 },
          pt: 1.25,
          pb: 0.5,
          ...(isDesktop && {
            ...TRACKER_HEADER_SX,
            pb: 0,
            borderBottom: 1,
            borderColor: "divider",
          }),
          display: "flex",
          alignItems: "center",
          gap: 1,
          "@media (max-width: 899.95px) and (max-height: 500px)": {
            display: "none",
          },
        }}
      >
        <Typography
          component="h2"
          id="satellites-title"
          sx={{
            fontSize: { xs: 12, md: 17 },
            fontWeight: 650,
            lineHeight: 1.25,
          }}
        >
          Satellites
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontVariantNumeric: "tabular-nums" }}
        >
          {satellites.length}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {isDesktop && (
          <IconButton aria-label="Close satellites" onClick={handleClosePanel}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "row", md: "column" },
          alignItems: { xs: "stretch", md: "initial" },
          justifyContent: "flex-start",
          gap: { xs: 0.75, md: 0.5 },
          overflowX: { xs: "auto", md: "hidden" },
          overflowY: { xs: "hidden", md: "auto" },
          flex: { xs: "none", md: 1 },
          minHeight: 0,
          pl: { xs: "max(12px, env(safe-area-inset-left, 0px))", md: 1.5 },
          pr: 1.5,
          pb: 1.5,
          pt: { xs: 0, md: 2.5 },
          scrollbarWidth: "thin",
          scrollbarColor: `${theme.palette.text.disabled} ${theme.palette.background.paper}`,
          "&::-webkit-scrollbar": { width: 8, height: 8 },
          "&::-webkit-scrollbar-track": {
            backgroundColor: theme.palette.background.paper,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.text.disabled,
            borderRadius: 8,
            border: `2px solid ${theme.palette.background.paper}`,
          },
          overscrollBehavior: "contain",
          WebkitOverflowScrolling: "touch",
          "@media (max-width: 899.95px) and (max-height: 500px)": {
            py: 1,
          },
        }}
      >
        {sortedSatellites.map((satellite) => {
          const isSelected = selectedSatelliteId === satellite.info.satid;
          return (
            <ButtonBase
              key={satellite.info.satid}
              onClick={(event) =>
                handleSatelliteSelect(satellite, event.currentTarget)
              }
              aria-label={`Focus on ${satellite.info.satname}`}
              aria-pressed={isSelected}
              sx={{
                display: "flex",
                textAlign: "left",
                justifyContent: "space-between",
                gap: 1.5,
                minHeight: 44,
                px: 1.5,
                py: 1,
                borderRadius: 1,
                flexShrink: 0,
                bgcolor: "action.hover",
                color: "text.primary",
                border: 1,
                borderColor: "transparent",
                transition:
                  "background-color 150ms ease, border-color 150ms ease",
                ...(isSelected && {
                  bgcolor: "action.selected",
                  borderColor: "divider",
                  color: "primary.main",
                }),
                "&:hover": { bgcolor: "action.selected" },
              }}
            >
              <Typography
                sx={{
                  display: { xs: "block", md: "none" },
                  fontSize: 12,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {getShortName(satellite.info.satname)}
              </Typography>
              <Box
                sx={{
                  display: { xs: "none", md: "flex" },
                  alignItems: "center",
                  gap: 1.5,
                  minWidth: 0,
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>
                  {getDisplayName(satellite.info.satname)}
                </Typography>
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={{ color: "text.disabled", flexShrink: 0 }}
                >
                  ·
                </Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "text.secondary",
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                  }}
                >
                  {GENERATION_LABELS[satellite.info.generation]}
                </Typography>
              </Box>
              <CenterFocusStrongIcon
                sx={{
                  display: { xs: "none", md: "block" },
                  fontSize: 17,
                  color: "text.secondary",
                  ...(isSelected && { color: "primary.main" }),
                }}
              />
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );

  if (isDesktop) {
    return (
      <Drawer
        anchor="right"
        variant="persistent"
        open={satellitesOpen}
        onClose={handleClosePanel}
        transitionDuration={
          reducedMotion || switchingPanels ? 0 : DRAWER_TRANSITION_MS
        }
        slots={{ transition: RightPanelTransition }}
        slotProps={{
          docked: { sx: RIGHT_PANEL_DOCKED_SX },
          paper: {
            id: "satellite-panel",
            role: "complementary",
            "aria-labelledby": "satellites-title",
            onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
              if (event.key === "Escape") handleClosePanel();
            },
            sx: {
              position: "relative",
              height: "100%",
              width: RIGHT_PANEL_WIDTH,
              maxWidth: "100vw",
              colorScheme: theme.palette.mode,
              borderColor: "divider",
            },
          },
          transition: {
            appear: true,
            easing: theme.transitions.easing.easeOut,
            onExited: handlePanelExited,
          },
        }}
      >
        {panel}
      </Drawer>
    );
  }

  return panel;
};
